import { getInitialSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'

import { getVarientId } from '@src/quince-site-common/src/hooks/cart/hooks/new-cart-helper'
import { cloneDeep } from '@src/utils/functions/utils'
import { addPurchaseInfoToProductConfigs, getProductInitialState } from './BundleProductPurchase.helpers'
import {
  BUNDLE_PRODUCT_TYPE,
  BundleAction,
  BundleActionType,
  BundleConfig,
  BundleProductConfig,
} from './BundleProductPurchase.types'

export default function bundleReducer(state: BundleConfig, action: BundleAction): BundleConfig {
  const { type, payload } = action

  switch (type) {
    case BundleActionType.UPDATE_SELECTED_OPTIONS: {
      const { index, optionName, optionValue, isOptional } = payload
      const productsToUpdate = isOptional ? state.optionalProducts : state.products
      const product = productsToUpdate[index]?.product
      const selectedOptions = productsToUpdate[index]?.selectedOptions
      const purchaseInfo = productsToUpdate[index]?.purchaseInfo
      const updatedSelectedOptions = productHelper.getUpdatedSelectedOptionsOnProductOptionSelect(
        product,
        selectedOptions,
        optionName,
        optionValue,
      )
      const variants = productHelper.getVariantsForOptions(product, updatedSelectedOptions)
      const pricing = productHelper.getProductPriceDetails(product, updatedSelectedOptions)
      const isOutOfStock =
        variants.length === 1 &&
        (purchaseInfo?.shopifyVariantIdVsShopifyVariantInfo[getVarientId(variants[0])]?.quantityAvailable || 0) <= 0
      const updatedProductConfig: BundleProductConfig = {
        ...productsToUpdate[index],
        variants: variants,
        selectedOptions: updatedSelectedOptions,
        error: isOutOfStock ? 'Out of stock' : null,
        salePrice: +pricing.formattedSalePrice,
        traditionalPrice: +(pricing.formattedTraditionalPrice || '0'),
      }
      const updatedConfig: BundleConfig = cloneDeep(state)
      updatedConfig[isOptional ? 'optionalProducts' : 'products'][index] = updatedProductConfig

      return updatedConfig
    }

    case BundleActionType.UPDATE_SELECTION: {
      const { index, checked } = payload
      const updatedConfig: BundleConfig = cloneDeep(state)
      if (updatedConfig.optionalProducts[index]) {
        updatedConfig.optionalProducts[index].selection.isSelected = checked
      }
      return updatedConfig
    }

    case BundleActionType.UPDATE_SIBLING_SELECTED_OPTIONS: {
      const { index, product: newSiblingProduct, isOptional, purchaseInfo, searchParams } = payload
      const products = isOptional ? state.optionalProducts : state.products
      const productToReplace = products[index]
      if (!productToReplace) {
        console.error('Unable to find product details for the current sibling product')
      }
      const prevProductSelection = productToReplace?.selection
      const prevSelectedOptions = productToReplace?.selectedOptions
      const updatedSelectedOptions = getInitialSelectedOptions(
        newSiblingProduct,
        prevSelectedOptions,
        false,
        false,
        prevSelectedOptions?.length,
      )
      const variants = productHelper.getVariantsForOptions(newSiblingProduct, updatedSelectedOptions)
      const pricing = productHelper.getProductPriceDetails(newSiblingProduct, updatedSelectedOptions)
      const isOutOfStock =
        variants.length === 1 &&
        (purchaseInfo?.shopifyVariantIdVsShopifyVariantInfo[getVarientId(variants[0])]?.quantityAvailable || 0) <= 0

      const initialConfigForSiblingProduct = getProductInitialState({
        product: newSiblingProduct,
        searchTerm: searchParams,
        type: isOptional ? BUNDLE_PRODUCT_TYPE.OPTIONAL : BUNDLE_PRODUCT_TYPE.DEFAULT,
      })
      const updatedConfigForNewSiblingProduct: BundleProductConfig = {
        ...initialConfigForSiblingProduct,
        variants: variants,
        selectedOptions: updatedSelectedOptions,
        purchaseInfo,
        error: isOutOfStock ? 'Out of stock' : null,
        salePrice: +pricing.formattedSalePrice,
        traditionalPrice: +(pricing.formattedTraditionalPrice || '0'),
        selection: prevProductSelection,
      }
      const updatedState = cloneDeep(state)
      updatedState[isOptional ? 'optionalProducts' : 'products'][index] = updatedConfigForNewSiblingProduct

      return updatedState
    }

    case BundleActionType.UPDATE_PRICING_INFO_FOR_ALL: {
      const { purchaseInfo } = payload
      const updatedState = cloneDeep(state)
      updatedState.products = addPurchaseInfoToProductConfigs(state.products, purchaseInfo)
      updatedState.optionalProducts = addPurchaseInfoToProductConfigs(state.optionalProducts, purchaseInfo)
      return updatedState
    }

    default:
      return state
  }
}
